import * as _ from 'lodash';
import * as Panel from './panel';
import * as PanelLib from './panellib/libpanel';
import * as Types from './types';
import * as PanelRegistry2 from './PanelRegistry';
import * as LibTypes from './panellib/libtypes';

const getTypeHandlerStacksInternal = (currentType: Types.Type) => {
  const stacks = LibTypes._getTypeHandlerStacks(
    currentType,
    PanelRegistry2.PanelSpecs,
    PanelRegistry2.ConverterSpecs,
    Types.isAssignableTo
  );
  // Hack, sort execute to the top for now.
  // TODO: Be better
  return _.sortBy(stacks, stack => (stack.id.startsWith('execute') ? 0 : 1));
};

// We memoize this, because its currently called a lot and is pretty
// expensive.
const typeHandlerCache: {
  [type: string]: ReturnType<typeof getTypeHandlerStacksInternal>;
} = {};

const getTypeHandlerStacks = (currentType: Types.Type) => {
  const typeId = JSON.stringify(currentType);
  let handler = typeHandlerCache[typeId];
  if (handler != null) {
    return handler;
  }
  handler = getTypeHandlerStacksInternal(currentType);
  typeHandlerCache[typeId] = handler;
  return handler;
};

export type PanelStack =
  | LibTypes.TypedInputHandlerStack<
      Types.Type,
      Panel.PanelSpec,
      Panel.PanelConvertSpec
    >
  | undefined;

// Get the panels available for a given type. If a panelId is already
// chosen, pass it as the second argument. The curPanelId will match
// panelId if it is available, otherwise it will revert to the first
// available panel.
export function getPanelStacksForType(
  type: Types.Type,
  panelId: string | undefined
): {
  curPanelId: string | undefined;
  stackIds: Array<{id: string; displayName: string}>;
  handler: PanelStack;
} {
  const handlerStacks = getTypeHandlerStacks(type).filter(
    hs => !PanelLib.getStackIdAndName(hs).id.endsWith('table2')
  );
  const stackIds = handlerStacks.map(PanelLib.getStackIdAndName);
  const stackIndex = stackIds.findIndex(si => si.id === panelId);

  const curPanelId =
    stackIndex !== -1
      ? stackIds[stackIndex].id
      : stackIds.length > 0
      ? stackIds[0].id
      : undefined;
  const handler =
    stackIndex !== -1 ? handlerStacks[stackIndex] : handlerStacks[0];
  return {curPanelId, stackIds, handler};
}

// Get fixed dimensions for a panel stack.
// Regular panels can optionally set a defaultFixedSize. Converter
// panels can specify a function that computes a fixed size based on
// their child panel dimensions. This runs the chain of calls to
// get the top-level dimensions.
export function getPanelStackDims(
  panelStack: PanelStack
): {width: number | undefined; height: number | undefined} {
  if (panelStack == null) {
    return {width: undefined, height: undefined};
  }
  if (PanelLib.isWithChild(panelStack)) {
    const childDims = getPanelStackDims(panelStack.child);
    if (panelStack.defaultFixedSize != null) {
      // TODO: converter panels should be able to change their fixed
      // size based on their config. (e.g. the multi-container should
      // change its fixed size based on its page size). We need to
      // wire the nested config through here to do that. (We already
      // do something similar in PanelComp)
      return panelStack.defaultFixedSize(childDims);
    }
    return {width: undefined, height: undefined};
  }
  return {
    height: panelStack.defaultFixedSize
      ? panelStack.defaultFixedSize.height
      : undefined,
    width: panelStack.defaultFixedSize
      ? panelStack.defaultFixedSize.width
      : undefined,
  };
}
